// E8CardsProbabilityPerimeterClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 6) - prawdopodobienstwo + obwod wielokatow (A/B oraz C/D).
// Karty: trojkaty rownoboczne, kwadraty, pieciokaty foremne. Losujemy jedna karte.

import React from "react";
import { ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function gcd(a, b) {
  return b ? gcd(b, a % b) : a;
}

function frac(num, den) {
  const g = gcd(num, den) || 1;
  const n = num / g;
  const d = den / g;
  return { num: n, den: d, latex: d === 1 ? `${n}` : `\\frac{${n}}{${d}}` };
}

function ChoiceBox({ labels }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        border: "1.5px solid currentColor",
        borderRadius: 4,
        padding: "1px 8px",
        margin: "0 4px",
        opacity: 0.75,
        verticalAlign: "middle",
        fontSize: "0.9em",
      }}
    >
      {labels.map((label, index) => (
        <React.Fragment key={label}>
          {index > 0 && <span style={{ opacity: 0.5 }}>|</span>}
          <strong>
            <InlineMath math={label} />
          </strong>
        </React.Fragment>
      ))}
    </span>
  );
}

function buildData({ tCount, sCount, pCount, tSide, sSide, pSide, threshold, shuffleOptions = true }) {
  const N = tCount + sCount + pCount;
  const tP = 3 * tSide;
  const sP = 4 * sSide;
  const pP = 5 * pSide;
  const maxP = Math.max(tP, sP, pP);

  const below = (limit) =>
    (tP < limit ? tCount : 0) + (sP < limit ? sCount : 0) + (pP < limit ? pCount : 0);

  // grupa 1: P(kwadrat)
  const g1correct = frac(sCount, N);
  const g1distr = frac(1, N);
  // grupa 2: P(obwod < threshold)
  const g2correct = frac(below(threshold), N);
  const g2distr = frac(below(maxP), N);

  let g1 = [
    { ...g1correct, correct: true },
    { ...g1distr, correct: false },
  ];
  let g2 = [
    { ...g2correct, correct: true },
    { ...g2distr, correct: false },
  ];

  if (shuffleOptions) {
    g1 = shuffle(g1);
    g2 = shuffle(g2);
  } else {
    g1 = [g1[1], g1[0]]; // dystraktor jako A, poprawna jako B (zgodnie z CKE)
  }

  const G1_LETTERS = ["A", "B"];
  const G2_LETTERS = ["C", "D"];

  return {
    tCount,
    sCount,
    pCount,
    tSide,
    sSide,
    pSide,
    threshold,
    N,
    tP,
    sP,
    pP,
    g1: g1.map((o, i) => ({ ...o, letter: G1_LETTERS[i] })),
    g2: g2.map((o, i) => ({ ...o, letter: G2_LETTERS[i] })),
    correct: {
      s1: G1_LETTERS[g1.findIndex((o) => o.correct)],
      s2: G2_LETTERS[g2.findIndex((o) => o.correct)],
    },
  };
}

function createOriginalTask() {
  return {
    type: "E8CardsProbabilityPerimeterClosedTask",
    data: buildData({
      tCount: 5, sCount: 4, pCount: 9,
      tSide: 6, sSide: 7, pSide: 2,
      threshold: 18, shuffleOptions: false,
    }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 6 },
    tags: ["e8", "prawdopodobienstwo", "obwod", "wielokaty"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const counts = pick([
    { tCount: 5, sCount: 4, pCount: 9 },
    { tCount: 6, sCount: 3, pCount: 9 },
    { tCount: 4, sCount: 5, pCount: 9 },
    { tCount: 3, sCount: 6, pCount: 9 },
  ]);
  const tSide = pick([6, 7]); // tP = 18 lub 21
  const sSide = pick([7, 8, 9]); // sP = 28, 32, 36 (najwiekszy)
  const pSide = 2; // pP = 10 (najmniejszy, < tP)
  return {
    type: "E8CardsProbabilityPerimeterClosedTask",
    data: buildData({ ...counts, tSide, sSide, pSide, threshold: 3 * tSide }),
    source: { variant: "random", basedOn: "E8_2025_CZE_6" },
    tags: ["e8", "prawdopodobienstwo", "obwod", "wielokaty"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        W pudełku jest <InlineMath math={`${data.N}`} /> kart jednakowej wielkości. Na
        każdej z nich narysowano jedną figurę geometryczną. Wśród tych kart
      </p>
      <ul style={{ marginTop: 0 }}>
        <li>jest <InlineMath math={`${data.tCount}`} /> kart z narysowanym trójkątem równobocznym o boku długości <InlineMath math={`${data.tSide}\\ \\text{cm}`} /></li>
        <li>są <InlineMath math={`${data.sCount}`} /> karty z narysowanym kwadratem o boku długości <InlineMath math={`${data.sSide}\\ \\text{cm}`} /></li>
        <li>jest <InlineMath math={`${data.pCount}`} /> kart z narysowanym pięciokątem foremnym o boku długości <InlineMath math={`${data.pSide}\\ \\text{cm}`} />.</li>
      </ul>
      <p>Z pudełka wylosowano jedną kartę.</p>
      <p>
        <strong>
          Uzupełnij zdania. Wybierz odpowiedź spośród oznaczonych literami{" "}
          <InlineMath math="A" /> i <InlineMath math="B" /> oraz odpowiedź spośród
          oznaczonych literami <InlineMath math="C" /> i <InlineMath math="D" />.
        </strong>
      </p>
      <p style={{ marginBottom: 4 }}>
        Prawdopodobieństwo, że wylosowano kartę z narysowanym kwadratem, jest równe{" "}
        <ChoiceBox labels={["A", "B"]} />.
      </p>
      <p style={{ marginTop: 0, marginLeft: 16 }}>
        {data.g1.map((o, index) => (
          <React.Fragment key={o.letter}>
            {index > 0 && <span style={{ marginLeft: 36 }} />}
            <ChoiceOptionMath letter={o.letter} math={o.latex} display />
          </React.Fragment>
        ))}
      </p>
      <p style={{ marginBottom: 4 }}>
        Prawdopodobieństwo, że wylosowano kartę z narysowaną figurą o obwodzie
        mniejszym od <InlineMath math={`${data.threshold}\\ \\text{cm}`} />, jest równe{" "}
        <ChoiceBox labels={["C", "D"]} />.
      </p>
      <p style={{ marginTop: 0, marginLeft: 16 }}>
        {data.g2.map((o, index) => (
          <React.Fragment key={o.letter}>
            {index > 0 && <span style={{ marginLeft: 36 }} />}
            <ChoiceOptionMath letter={o.letter} math={o.latex} display />
          </React.Fragment>
        ))}
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const answer = userAnswer || {};
  return (
    <div style={{ display: "grid", rowGap: 14, marginTop: 10 }}>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math={data.g1[0].letter} /> lub{" "}
          <InlineMath math={data.g1[1].letter} />:
        </div>
        <ABCDInput
          options={data.g1.map((o) => ({ label: o.letter, textNode: <InlineMath math={o.letter} /> }))}
          value={answer.s1 ?? null}
          onChange={(s1) => onChange({ ...answer, s1 })}
          disabled={disabled}
        />
      </div>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math={data.g2[0].letter} /> lub{" "}
          <InlineMath math={data.g2[1].letter} />:
        </div>
        <ABCDInput
          options={data.g2.map((o) => ({ label: o.letter, textNode: <InlineMath math={o.letter} /> }))}
          value={answer.s2 ?? null}
          onChange={(s2) => onChange({ ...answer, s2 })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    data.g1[userAnswer?.s1]?.letter === data.correct.s1 &&
    data.g2[userAnswer?.s2]?.letter === data.correct.s2
  );
}

export function renderAnswer({ data }) {
  return <InlineMath math={`\\text{${data.correct.s1}},\\ \\text{${data.correct.s2}}`} />;
}

export function renderUserAnswer(userAnswer, { data }) {
  const l1 = data.g1[userAnswer?.s1]?.letter;
  const l2 = data.g2[userAnswer?.s2]?.letter;
  if (!l1 && !l2) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`\\text{${l1 || "-"}},\\ \\text{${l2 || "-"}}`} />;
}

export function renderExplanation({ data }) {
  const g1c = data.g1.find((o) => o.correct);
  const g2c = data.g2.find((o) => o.correct);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Grupa A/B.</strong> Kart z kwadratem jest{" "}
        <InlineMath math={`${data.sCount}`} /> na <InlineMath math={`${data.N}`} />, więc
      </p>
      <p style={{ marginLeft: 12 }}>
        <InlineMath math={`P=\\frac{${data.sCount}}{${data.N}}=${g1c.latex}`} /> — odpowiedź{" "}
        <InlineMath math={`\\text{${data.correct.s1}}`} />.
      </p>
      <p>
        <strong>Grupa C/D.</strong> Obwody: trójkąt <InlineMath math={`${data.tP}\\ \\text{cm}`} />,
        kwadrat <InlineMath math={`${data.sP}\\ \\text{cm}`} />, pięciokąt{" "}
        <InlineMath math={`${data.pP}\\ \\text{cm}`} />. Mniejszy od{" "}
        <InlineMath math={`${data.threshold}\\ \\text{cm}`} /> jest tylko pięciokąt, więc
      </p>
      <p style={{ marginLeft: 12 }}>
        <InlineMath math={`P=\\frac{${data.pCount}}{${data.N}}=${g2c.latex}`} /> — odpowiedź{" "}
        <InlineMath math={`\\text{${data.correct.s2}}`} />.
      </p>
    </div>
  );
}
